import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("hr", { class: "my-3" }, null, -1)
const _hoisted_2 = {
  key: 0,
  class: "animate-spin"
}
const _hoisted_3 = { class: "w-full" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", { class: "mt-3 pl-1" }, "Informações Adicionais", -1)
const _hoisted_5 = { class: "overflow-y-auto h-60 pr-0.5 w-full" }
const _hoisted_6 = { class: "col-span-1 max-sm:col-span-2 flex max-sm:mr-4 max-sm:justify-center sm:justify-end space-x-2 row-span-3" }
const _hoisted_7 = { class: "flex flex-row justify-between items-center" }
const _hoisted_8 = { class: "text-left" }
const _hoisted_9 = { class: "mt-2" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", { class: "mt-2" }, [
  /*#__PURE__*/_createTextVNode(" Essa é uma ação "),
  /*#__PURE__*/_createElementVNode("strong", null, "irreversível"),
  /*#__PURE__*/_createTextVNode(". ")
], -1)
const _hoisted_12 = { class: "flex justify-end py-2" }
const _hoisted_13 = { key: 2 }

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Customer } from '@/http/customerSession';
import { Settings, CircleCheck, CircleX, Pencil, Redo2, Trash2, LoaderCircle, Check } from 'lucide-vue-next';
import { ref, watch, onMounted } from 'vue';
import { DELETE_CUSTOMER_ATTRIBUTES, PATCH_CUSTOMER_FIELD, STORAGE_CREDENTIALS } from '@/store';
import { useStore } from 'vuex';
import { getCostumerById } from '@/http/customer';
import { get, set } from '@vueuse/core';

const NAME_ATTRIBUTE_KEY = 'name';
type AttributeTuple = {
    key?: string,
    originalKey?: string,
    value?: string,
    originalValue?: string,
    isEditing: boolean,
    hasValueError: boolean,
    hasNameError: boolean,
    showProgressIndicator: boolean,
    showSuccessIndicator: boolean,
    deleted: boolean
};
type CustomerAttributeForm = {
    [NAME_ATTRIBUTE_KEY]?: string,
    attributes: AttributeTuple[]
};

export default /*#__PURE__*/_defineComponent({
  __name: 'PartnerAttributes',
  props: {
    customerId: {
        type: String,
        required: true
    },
},
  emits: ['updated-customer-name'],
  setup(__props, { emit: __emit }) {

const openDialog = ref(false);
const emit = __emit;
const { getters, dispatch } = useStore();
const props = __props;

const parseAttributesToListOfTuplesWithoutName = (attributes: Record<string, string> | undefined): AttributeTuple[] => {
    return Object.entries(attributes ?? [])
        .map(([key, value]): AttributeTuple => ({
            key, value, originalKey: key, originalValue: value, isEditing: false, hasNameError: false, hasValueError: false, deleted: false, showProgressIndicator: false, showSuccessIndicator: false
        }))
        .filter((attr: AttributeTuple) => attr.key !== NAME_ATTRIBUTE_KEY);
};
const customer = ref<Customer | undefined>();
const customerAttributes = ref<CustomerAttributeForm>({
    [NAME_ATTRIBUTE_KEY]: customer.value?.partnerAttributes?.name,
    attributes: parseAttributesToListOfTuplesWithoutName(customer.value?.partnerAttributes),
});
const showNameError = ref(false);

const isEmpty = (str?: string) => { return (str ?? '').trim() === ''; };

const isKeyValid = (key?: string) => {
    if (isEmpty(key)) return false;
    if (key === NAME_ATTRIBUTE_KEY) return false;
    if (key !== undefined && !(/^[a-zA-Z0-9 _-]+$/).test(key)) {
        return false;
    }
    const allTuplesWithCreatedOnes = get(customerAttributes).attributes.filter(anAttribute => anAttribute.key === key);
    return allTuplesWithCreatedOnes.length === 1;
};

const validateNameOfIndex = (index: number) => {
    const attributeTuple = get(customerAttributes).attributes[index];
    attributeTuple.hasNameError = !isKeyValid(attributeTuple.key);
};
const validateValueOfIndex = (index: number) => {
    const attributeTuple = get(customerAttributes).attributes[index];
    attributeTuple.hasValueError = isEmpty(attributeTuple.value) && (attributeTuple.value?.length ?? 0) <= 2000;
};

const saveAttribute = async (index: number) => {
    const customerRef = get(customer);
    if (customerRef === undefined) return;

    const attributeTuple = get(customerAttributes).attributes[index];

    validateNameOfIndex(index);
    validateValueOfIndex(index);
    if (attributeTuple.hasNameError || attributeTuple.hasValueError) {
        return;
    }
    if (attributeTuple.key === undefined) return;

    if (attributeTuple.originalKey !== undefined && attributeTuple.originalKey !== attributeTuple.key) {
        await deleteAttribute(attributeTuple.originalKey);
    }

    attributeTuple.showProgressIndicator = true;
    try {
        await dispatch(PATCH_CUSTOMER_FIELD, {
            partnerId: customerRef.associatedPartnerId,
            customerKey: customerRef.key,
            partnerAttributes: {
                [attributeTuple.key]: attributeTuple.value
            }
        });
    } catch (err) {
        attributeTuple.hasNameError = true;
        attributeTuple.hasValueError = true;
        attributeTuple.showProgressIndicator = false;
        return;
    }
    setTimeout(() => {
        attributeTuple.showProgressIndicator = false;
        attributeTuple.showSuccessIndicator = true;
        setTimeout(() => {
            attributeTuple.showSuccessIndicator = false;
        }, 1000);
    }, 1000);

    attributeTuple.originalKey = attributeTuple.key;
    attributeTuple.originalValue = attributeTuple.value;
    attributeTuple.isEditing = false;
    attributeTuple.deleted = false;
};

const showCustomerProgressIndicator = ref(false);
const showCustomerSuccessIndicator = ref(false);
const saveCustomer = async () => {
    if (customer.value === undefined) return;

    set(showNameError, isEmpty(customerAttributes.value[NAME_ATTRIBUTE_KEY]));
    let allValid = !get(showNameError);
    if (allValid === false) return;
    customerAttributes.value.attributes.forEach((attribute, index) => {
        const hasNotEdited = attribute.originalKey === attribute.key
            && attribute.originalValue === attribute.originalValue;
        if (attribute.deleted === true || attribute.isEditing !== true || hasNotEdited) return;
        if (attribute.originalKey !== NAME_ATTRIBUTE_KEY)
            validateNameOfIndex(index);
        validateValueOfIndex(index);
        allValid = allValid && !(attribute.hasNameError || attribute.hasValueError);
    });
    if (!allValid) return;

    const customerRef = get(customer);
    if (customerRef === undefined) return;

    try {
        set(showCustomerProgressIndicator, true);
        await dispatch(PATCH_CUSTOMER_FIELD, {
            partnerId: customerRef.associatedPartnerId,
            customerKey: customerRef.key,
            partnerAttributes: {
                [NAME_ATTRIBUTE_KEY]: customerAttributes.value[NAME_ATTRIBUTE_KEY]
            }
        });
    } catch (err) {
        set(showNameError, true);
        return;
    }

    setTimeout(() => {
        set(showCustomerProgressIndicator, false);
        set(showCustomerSuccessIndicator, true);
        setTimeout(() => {
            set(showCustomerSuccessIndicator, false);
        }, 1000);
    }, 500);

    const attributes = get(customerAttributes).attributes;

    for (let index = 0; index < attributes.length; index++) {
        const attribute = attributes[index];
        const hasNotEdited = attribute.originalKey === attribute.key
            && attribute.value === attribute.originalValue;
        if (attribute.deleted === true || attribute.isEditing !== true || hasNotEdited) {
            continue;
        }
        await saveAttribute(index);
    }

    await queryAndSetCustomer(customer.value.id);

    emit('updated-customer-name', customerAttributes.value[NAME_ATTRIBUTE_KEY]);
};

const deleteAttribute = async (attributeKey?: string, index?: number) => {
    if (customer.value?.id === undefined) return;
    if (attributeKey === undefined) return;
    await dispatch(DELETE_CUSTOMER_ATTRIBUTES, {
        customerId: customer.value?.id,
        customerAttribute: attributeKey
    });
    if (index === undefined) return;
    customerAttributes.value.attributes[index].deleted = true;
};

const queryAndSetCustomer = async (customerId?: string) => {
    let credentials = getters[STORAGE_CREDENTIALS];
    if (credentials === undefined || customerId === undefined) {
        throw new Error("Credentials are not present");
    }
    set(customer, await getCostumerById(customerId, credentials));
    set(customerAttributes, {
        name: customer.value?.partnerAttributes?.[NAME_ATTRIBUTE_KEY],
        attributes: parseAttributesToListOfTuplesWithoutName(customer.value?.partnerAttributes)
    });
};

onMounted(async () => {
    await queryAndSetCustomer(props.customerId);
});

watch(
    () => props.customerId,
    async () => {
        await queryAndSetCustomer(props.customerId);
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    open: openDialog.value,
    "onUpdate:open": _cache[4] || (_cache[4] = ($event: any) => ((openDialog).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogTrigger), { "as-child": "" }, {
        default: _withCtx(() => [
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "icon"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Settings))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (customer.value === undefined)
        ? (_openBlock(), _createBlock(_unref(DialogContent), { key: 0 }))
        : (_openBlock(), _createBlock(_unref(DialogContent), {
            key: 1,
            class: "p-4 sm:max-w-[700px] flex flex-col"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogHeader), { class: "flex flex-col flex-grow" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogTitle), { class: "text-gray-700 max-sm:text-left" }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Editar Cliente ")
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(DialogClose), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Button), {
                        variant: "ghost",
                        class: "absolute right-2 top-2"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(CircleX), { class: "text-gray-300" })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _hoisted_1
                ]),
                _: 1
              }),
              (customer.value === undefined)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2))
                : (_openBlock(), _createElementBlock("form", {
                    key: 1,
                    onSubmit: _withModifiers(saveCustomer, ["prevent"])
                  }, [
                    _createVNode(_unref(Label), {
                      for: "name",
                      class: "pt-1 pl-1"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Nome")
                      ]),
                      _: 1
                    }),
                    _createElementVNode("span", {
                      class: _normalizeClass(["text-red-500 text-[10px]", { 'invisible': !showNameError.value }])
                    }, " * ", 2),
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_unref(Input), {
                        class: _normalizeClass([{ 'border-red-500 placeholder:text-red-500': showNameError.value }, "my-1 ml-0.5"]),
                        name: "name",
                        modelValue: customerAttributes.value[NAME_ATTRIBUTE_KEY],
                        "onUpdate:modelValue": [
                          _cache[0] || (_cache[0] = ($event: any) => ((customerAttributes.value[NAME_ATTRIBUTE_KEY]) = $event)),
                          _cache[1] || (_cache[1] = () => {
                            showNameError.value = isEmpty(customerAttributes.value[NAME_ATTRIBUTE_KEY]);
                        })
                        ],
                        type: "text",
                        placeholder: "Nome"
                      }, null, 8, ["class", "modelValue"])
                    ]),
                    _hoisted_4,
                    _createElementVNode("div", _hoisted_5, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customerAttributes.value.attributes, (attributeTuple, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: _normalizeClass(["ml-0.5 grid grid-cols-7 grid-rows-4 gap-2 items-center w-[99%] gap-x-3", {
                            'hidden': attributeTuple.deleted === true,
                        }])
                        }, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["max-h-2 col-span-2 text-red-500 text-[10px]", attributeTuple.hasNameError ? 'visible' : 'invisible'])
                          }, " O atributo deve ser válido ", 2),
                          _createElementVNode("span", {
                            class: _normalizeClass(["max-h-2 col-span-4 text-red-500 text-[10px]", attributeTuple.hasValueError ? 'visible' : 'invisible'])
                          }, " Você precisa inserir o valor do atributo ", 2),
                          _createVNode(_unref(Label), {
                            for: String(index)
                          }, null, 8, ["for"]),
                          _createVNode(_unref(Input), {
                            type: "text",
                            name: `KEY_${index}`,
                            placeholder: "Propriedade",
                            modelValue: customerAttributes.value.attributes[index].key,
                            "onUpdate:modelValue": [($event: any) => ((customerAttributes.value.attributes[index].key) = $event), () => validateNameOfIndex(index)],
                            class: _normalizeClass(["col-span-2 row-span-3 self-end placeholder:text-gray-500", {
                                'bg-gray-200 border-gray-200': attributeTuple.isEditing !== true,
                                'border-red-500 placeholder:text-red-500 ring-red-500': attributeTuple.hasNameError,
                            }]),
                            readonly: attributeTuple.isEditing !== true
                          }, null, 8, ["name", "modelValue", "onUpdate:modelValue", "readonly", "class"]),
                          _createVNode(_unref(Input), {
                            type: "text",
                            name: `VAL_${index}`,
                            placeholder: "Valor",
                            modelValue: customerAttributes.value.attributes[index].value,
                            "onUpdate:modelValue": [($event: any) => ((customerAttributes.value.attributes[index].value) = $event), () => validateValueOfIndex(index)],
                            class: _normalizeClass(["col-span-4 max-sm:col-span-3 row-span-3 self-end placeholder:text-gray-500", {
                                'bg-gray-200 border-gray-200': attributeTuple.isEditing !== true,
                                'border-red-500 placeholder:text-red-500 ring-red-500': attributeTuple.hasValueError,
                            }]),
                            readonly: attributeTuple.isEditing !== true
                          }, null, 8, ["name", "modelValue", "onUpdate:modelValue", "readonly", "class"]),
                          _createElementVNode("div", _hoisted_6, [
                            (!attributeTuple.isEditing)
                              ? (_openBlock(), _createBlock(_unref(Dialog), { key: 0 }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(DialogTrigger), { "as-child": "" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                          type: "button",
                                          variant: "ghost",
                                          size: "icon"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(Trash2), { class: "text-slate-400" })
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_unref(DialogContent), { class: "sm:max-w-[425px]" }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(DialogHeader), null, {
                                          default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_7, [
                                              _createVNode(_unref(DialogTitle), null, {
                                                default: _withCtx(() => [
                                                  _createTextVNode("Deletar Informações Adicionais")
                                                ]),
                                                _: 1
                                              }),
                                              _createVNode(_unref(DialogClose), null, {
                                                default: _withCtx(() => [
                                                  _createVNode(_unref(Button), { variant: "ghost" }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_unref(CircleX), { class: "text-slate-400" })
                                                    ]),
                                                    _: 1
                                                  })
                                                ]),
                                                _: 1
                                              })
                                            ]),
                                            _createVNode(_unref(DialogDescription), null, {
                                              default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_8, [
                                                  _createElementVNode("p", _hoisted_9, [
                                                    _createTextVNode(" Você tem certeza de que deseja deletar este "),
                                                    _hoisted_10,
                                                    _createTextVNode("Atributo: "),
                                                    _createElementVNode("strong", null, _toDisplayString(attributeTuple.originalKey), 1),
                                                    _createTextVNode(" com valor: "),
                                                    _createElementVNode("strong", null, _toDisplayString(attributeTuple.originalValue), 1),
                                                    _createTextVNode("? ")
                                                  ]),
                                                  _hoisted_11
                                                ])
                                              ]),
                                              _: 2
                                            }, 1024)
                                          ]),
                                          _: 2
                                        }, 1024),
                                        _createVNode(_unref(DialogFooter), null, {
                                          default: _withCtx(() => [
                                            _createVNode(_unref(DialogClose), null, {
                                              default: _withCtx(() => [
                                                _createVNode(_unref(Button), {
                                                  type: "button",
                                                  variant: "ghost"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode(" Cancelar ")
                                                  ]),
                                                  _: 1
                                                })
                                              ]),
                                              _: 1
                                            }),
                                            _createVNode(_unref(DialogClose), null, {
                                              default: _withCtx(() => [
                                                _createVNode(_unref(Button), {
                                                  type: "button",
                                                  variant: "destructive",
                                                  onClick: async () => await deleteAttribute(attributeTuple.originalKey, index)
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode(" Deletar ")
                                                  ]),
                                                  _: 2
                                                }, 1032, ["onClick"])
                                              ]),
                                              _: 2
                                            }, 1024)
                                          ]),
                                          _: 2
                                        }, 1024)
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]),
                                  _: 2
                                }, 1024))
                              : (_openBlock(), _createBlock(_unref(Button), {
                                  key: 1,
                                  variant: "ghost",
                                  size: "icon",
                                  onClick: () => {
                                    attributeTuple.isEditing = false;
                                    if (attributeTuple.originalKey === undefined) {
                                        attributeTuple.deleted = true;
                                        return;
                                    }

                                    attributeTuple.key = attributeTuple.originalKey;
                                    attributeTuple.value = attributeTuple.originalValue;
                                    attributeTuple.hasNameError = false;
                                    attributeTuple.hasValueError = false;
                                }
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(Redo2), { class: "text-slate-400" })
                                  ]),
                                  _: 2
                                }, 1032, ["onClick"])),
                            (!attributeTuple.isEditing)
                              ? (_openBlock(), _createBlock(_unref(Button), {
                                  key: 2,
                                  type: "button",
                                  class: _normalizeClass({
                                    'bg-success': attributeTuple.showSuccessIndicator
                                }),
                                  variant: "default",
                                  size: "icon",
                                  disabled: attributeTuple.showProgressIndicator || attributeTuple.showSuccessIndicator,
                                  onClick: () => { attributeTuple.isEditing = true; }
                                }, {
                                  default: _withCtx(() => [
                                    (attributeTuple.showProgressIndicator)
                                      ? (_openBlock(), _createBlock(_unref(LoaderCircle), {
                                          key: 0,
                                          class: "animate-spin text-white"
                                        }))
                                      : (attributeTuple.showSuccessIndicator)
                                        ? (_openBlock(), _createBlock(_unref(Check), {
                                            key: 1,
                                            class: "animate-pulse text-white"
                                          }))
                                        : (_openBlock(), _createBlock(_unref(Pencil), {
                                            key: 2,
                                            class: "text-white"
                                          }))
                                  ]),
                                  _: 2
                                }, 1032, ["class", "disabled", "onClick"]))
                              : (_openBlock(), _createBlock(_unref(Button), {
                                  key: 3,
                                  type: "button",
                                  variant: "default",
                                  size: "icon",
                                  onClick: async () => await saveAttribute(index),
                                  disabled: attributeTuple.showProgressIndicator
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(CircleCheck), { class: "text-white" })
                                  ]),
                                  _: 2
                                }, 1032, ["onClick", "disabled"]))
                          ])
                        ], 2))
                      }), 128)),
                      _createElementVNode("div", _hoisted_12, [
                        _createVNode(_unref(Button), {
                          type: "button",
                          variant: "outline",
                          class: "col-span-3 max-sm:mr-7",
                          onClick: _cache[2] || (_cache[2] = () => {
                                customerAttributes.value.attributes.push({
                                    deleted: false,
                                    isEditing: true,
                                    hasNameError: false,
                                    hasValueError: false,
                                    showProgressIndicator: false,
                                    showSuccessIndicator: false,
                                });
                            })
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Adicionar Mais Informações ")
                          ]),
                          _: 1
                        })
                      ])
                    ]),
                    _createVNode(_unref(DialogFooter), { class: "flex justify-end space-x-2 border-t-2 border-gray-200 max-sm:flex-row" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(DialogClose), null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Button), {
                              type: "button",
                              variant: "outline",
                              size: "large",
                              onClick: _cache[3] || (_cache[3] = () => openDialog.value = false),
                              class: "mt-2"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Cancelar ")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_unref(Button), {
                          type: "submit",
                          variant: "default",
                          size: "large",
                          class: _normalizeClass(["rounded-full text-white mt-2", {
                            'animate-in animate-out bg-success': showCustomerSuccessIndicator.value
                        }])
                        }, {
                          default: _withCtx(() => [
                            (showCustomerProgressIndicator.value)
                              ? (_openBlock(), _createBlock(_unref(LoaderCircle), {
                                  key: 0,
                                  class: "animate-spin text-white"
                                }))
                              : (showCustomerSuccessIndicator.value)
                                ? (_openBlock(), _createBlock(_unref(Check), {
                                    key: 1,
                                    class: "animate-pulse text-white"
                                  }))
                                : (_openBlock(), _createElementBlock("span", _hoisted_13, "Salvar"))
                          ]),
                          _: 1
                        }, 8, ["class"])
                      ]),
                      _: 1
                    })
                  ], 32))
            ]),
            _: 1
          }))
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})